import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BrandingProvider } from './contexts/BrandingContext';
import { VerticalProvider } from './contexts/VerticalContext';
import { Toaster } from 'sonner';
import { ROLE_GROUPS, getHomeRouteByRole, hasAnyRole } from './lib/rbac';
import { MetricDetailProvider } from './components/common/MetricDetail';
import './App.css';

// One chunk per role's console: an employee never downloads the superadmin
// console, and the sign-in page carries none of them.
const LoginPage = lazy(() => import('./pages/LoginPage'));
// Public: where the onboarding invite / reset link lands.
const SetPasswordPage = lazy(() => import('./pages/SetPasswordPage'));
const EmployeeDashboard = lazy(() => import('./pages/EmployeeDashboard'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const ManagerDashboard = lazy(() => import('./pages/ManagerDashboard'));
const SuperadminDashboard = lazy(() => import('./pages/SuperadminDashboard'));

// The metric detail view navigates with the router it is rendered under.
const RoutedMetricDetailProvider = ({ children }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return <MetricDetailProvider navigate={navigate} pathname={pathname}>{children}</MetricDetailProvider>;
};

const FullScreenSpinner = () => (
  <div className="flex items-center justify-center h-screen bg-background">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

const PrivateRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <FullScreenSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (allowedRoles && !hasAnyRole(user.role, allowedRoles)) {
    return <Navigate to={getHomeRouteByRole(user.role)} />;
  }

  return children;
};

const AppRoutes = () => {
  const { user } = useAuth();

  const getDashboardForRole = (role) => {
    switch (role) {
      case 'superadmin':
        return <SuperadminDashboard />;
      case 'workspace_admin':
      case 'admin': // legacy support
      case 'hr_admin':
        return <AdminDashboard />;
      case 'manager':
      case 'hr_manager':
        return <ManagerDashboard />;
      case 'employee':
      default:
        return <EmployeeDashboard />;
    }
  };

  return (
    <Routes>
      <Route path="/login" element={
        user ? <Navigate to={getHomeRouteByRole(user.role)} /> : <LoginPage />
      } />
      <Route path="/set-password" element={<SetPasswordPage />} />
      
      <Route path="/dashboard/*" element={
        <PrivateRoute>
          {getDashboardForRole(user?.role)}
        </PrivateRoute>
      } />
      
      <Route path="/admin/*" element={
        <PrivateRoute allowedRoles={ROLE_GROUPS.admin}>
          <AdminDashboard />
        </PrivateRoute>
      } />

      <Route path="/manager/*" element={
        <PrivateRoute allowedRoles={ROLE_GROUPS.manager}>
          <ManagerDashboard />
        </PrivateRoute>
      } />

      <Route path="/superadmin/*" element={
        <PrivateRoute allowedRoles={ROLE_GROUPS.superadmin}>
          <SuperadminDashboard />
        </PrivateRoute>
      } />

      <Route path="/" element={<Navigate to={user ? getHomeRouteByRole(user.role) : "/login"} />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      {/* Inside AuthProvider: the theme is per workspace, so it can only be
          fetched once we know whose workspace it is. Outside Router so a route
          change never re-applies the palette. */}
      <BrandingProvider>
        <VerticalProvider>
          <Router>
            <div className="App">
              <RoutedMetricDetailProvider>
                <Suspense fallback={<FullScreenSpinner />}>
                  <AppRoutes />
                </Suspense>
              </RoutedMetricDetailProvider>
              <Toaster position="top-right" richColors />
            </div>
          </Router>
        </VerticalProvider>
      </BrandingProvider>
    </AuthProvider>
  );
}

export default App;
